import React from 'react';
import { Box, Card, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

/**
 * Renders record collections as a table on wide screens and as cards on
 * narrow ones.
 *
 * A five-column table in a 375px viewport can only be read by scrolling
 * sideways, which hides exactly the columns that matter (the rates). Cards
 * keep every field visible and give the row actions a real touch target.
 */

export interface DataField<T> {
  label: string;
  render: (row: T) => React.ReactNode;
  /** Shown as the card title instead of a labelled field. */
  primary?: boolean;
}

interface DataListProps<T> {
  rows: T[];
  fields: DataField<T>[];
  keyOf: (row: T) => React.Key;
  actions?: (row: T) => React.ReactNode;
  empty?: React.ReactNode;
  table: React.ReactNode;
}

export function DataList<T>({ rows, fields, keyOf, actions, empty, table }: DataListProps<T>) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  if (!isNarrow) return <>{table}</>;

  if (rows.length === 0) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {empty ?? 'Aucun élément.'}
        </Typography>
      </Card>
    );
  }

  const primary = fields.find((f) => f.primary);
  const rest = fields.filter((f) => !f.primary);

  return (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Card key={keyOf(row)} sx={{ p: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1, minWidth: 0 }}>
              {primary ? primary.render(row) : null}
            </Typography>
            {actions && (
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>{actions(row)}</Box>
            )}
          </Box>
          <Divider sx={{ my: 1.25 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 1.25,
            }}
          >
            {rest.map((field) => (
              <Box key={field.label} sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  {field.label}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {field.render(row)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>
      ))}
    </Stack>
  );
}

export default DataList;
